import Filmstrip from '../Filmstrip'
import { IconTrophy, IconHome } from '../icons'

function fmtTime(secs) {
  if (secs == null) return '--:--'
  const m = String(Math.floor(secs / 60)).padStart(2, '0')
  const s = String(secs % 60).padStart(2, '0')
  return `${m}:${s}`
}

/**
 * Phone screen shown after a player finishes in Battle Royale.
 * Shows personal result + live status of other players.
 * When party transitions to 'results', shows final ranking.
 * When party transitions to 'lobby', onBackToLobby returns to lobby.
 * When party transitions to 'ended', shows thanks screen.
 */
export default function PartyBattleDone({ party, currentUserId, chain, finalStats, onLeave, onBackToLobby }) {
  const players = party?.players ?? {}
  const isResults  = party?.status === 'results'
  const isLobby    = party?.status === 'lobby'
  const isEnded    = party?.status === 'ended'
  const isSpeedRun = party?.mode === 'speed-run'

  const myResult = players[currentUserId]
  const myHops = finalStats?.moves ?? myResult?.hops ?? 0
  const myTime = finalStats?.time ?? myResult?.time ?? 0

  // Rank finished players — Speed Run uses finish order, Battle Royale uses hops then time
  const finished = Object.entries(players)
    .filter(([, p]) => p.status === 'finished')
    .sort((a, b) => {
      if (isSpeedRun) {
        const aT = a[1].finishedAt?.seconds ?? a[1].finishedAt?.toMillis?.() ?? 9999999999
        const bT = b[1].finishedAt?.seconds ?? b[1].finishedAt?.toMillis?.() ?? 9999999999
        if (aT !== bT) return aT - bT
        return (a[1].time ?? 9999) - (b[1].time ?? 9999)
      }
      const diff = (a[1].hops ?? 999) - (b[1].hops ?? 999)
      if (diff !== 0) return diff
      return (a[1].time ?? 9999) - (b[1].time ?? 9999)
    })

  const stillPlaying = Object.entries(players).filter(
    ([, p]) => p.status === 'playing' || !p.status
  )

  const myRank = finished.findIndex(([uid]) => uid === currentUserId) + 1
  const winnerEntry = finished[0]

  // ── Host ended the party ──────────────────────────────────────────────────
  if (isEnded) {
    return (
      <div className="party-battle-done">
        <p className="party-battle-done__headline">Thanks for playing!</p>
        <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>
          The host has ended the party.
        </p>
        <button
          className="btn-primary"
          onClick={onLeave}
          style={{ marginTop: 24, display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <IconHome size={18} aria-hidden="true" />
          <span>Back to Home</span>
        </button>
      </div>
    )
  }

  // ── Host started a new round — return to lobby ────────────────────────────
  if (isLobby) {
    return (
      <div className="party-battle-done">
        <p className="party-battle-done__headline">New Round!</p>
        <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>
          The host is starting another game.
        </p>
        <button className="btn-primary" onClick={onBackToLobby} style={{ marginTop: 24 }}>
          Back to Lobby
        </button>
      </div>
    )
  }

  // ── Final results ─────────────────────────────────────────────────────────
  if (isResults) {
    return (
      <div className="party-battle-done">
        <p
          className="party-battle-done__headline"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
        >
          {myRank === 1 ? (
            <>
              <IconTrophy size={26} aria-hidden="true" />
              <span>You Won!</span>
            </>
          ) : myRank > 0
            ? `#${myRank} — Nice work!`
            : 'Game Over'}
        </p>

        <div className="party-battle-done__my-stats">
          {isSpeedRun ? (
            <span className="party-battle-done__stat-big">{fmtTime(myTime)}</span>
          ) : (
            <>
              <span className="party-battle-done__stat-big">{myHops}</span>
              <span className="party-battle-done__stat-label">hop{myHops !== 1 ? 's' : ''}</span>
              <span className="party-battle-done__stat-time">{fmtTime(myTime)}</span>
            </>
          )}
        </div>

        {myRank !== 1 && winnerEntry && (
          <p className="party-battle-done__winner-note">
            Winner: {winnerEntry[1].username || 'Guest'}{' '}
            ({isSpeedRun ? fmtTime(winnerEntry[1].time) : `${winnerEntry[1].hops} hops`})
          </p>
        )}

        {chain && chain.length > 0 && (
          <div className="party-battle-done__chain-wrap">
            <Filmstrip chain={chain} onItemClick={() => {}} hidePlaceholder />
          </div>
        )}

        <p className="party-battle-done__others-label" style={{ marginTop: 16 }}>
          Waiting for host...
        </p>

        <button className="btn-ghost" onClick={onBackToLobby} style={{ marginTop: 16 }}>
          Back to Lobby
        </button>
      </div>
    )
  }

  // ── Waiting for others to finish ──────────────────────────────────────────
  return (
    <div className="party-battle-done">
      <p className="party-battle-done__headline">You finished!</p>

      <div className="party-battle-done__my-stats">
        {isSpeedRun ? (
          <span className="party-battle-done__stat-big">{fmtTime(myTime)}</span>
        ) : (
          <>
            <span className="party-battle-done__stat-big">{myHops}</span>
            <span className="party-battle-done__stat-label">hop{myHops !== 1 ? 's' : ''}</span>
            <span className="party-battle-done__stat-time">{fmtTime(myTime)}</span>
          </>
        )}
      </div>

      {chain && chain.length > 0 && (
        <div className="party-battle-done__chain-wrap">
          <Filmstrip chain={chain} onItemClick={() => {}} hidePlaceholder />
        </div>
      )}

      <div className="party-battle-done__others">
        <p className="party-battle-done__others-label">
          {stillPlaying.length > 0
            ? `Waiting for ${stillPlaying.length} player${stillPlaying.length !== 1 ? 's' : ''}...`
            : 'All players finished!'}
        </p>
        {finished.length > 0 && (
          <div className="party-battle-done__leaderboard">
            {finished.map(([uid, p], i) => (
              <div key={uid} className="party-battle-done__lb-row">
                <span className="party-battle-done__lb-rank">#{i + 1}</span>
                <span className="party-battle-done__lb-name">
                  {p.username || 'Guest'}
                  {uid === currentUserId ? ' (you)' : ''}
                </span>
                <span className="party-battle-done__lb-stats">
                  {isSpeedRun ? fmtTime(p.time) : `${p.hops} hops · ${fmtTime(p.time)}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
